'use client'

import { useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  X, Clock, ArrowRight, ChevronDown,
  Target, CalendarDays, ClipboardList, Zap, Receipt, RefreshCw,
  Megaphone, MessageCircle, Smartphone,
  Calendar, Bell, ListOrdered,
  FolderOpen, Shield, Mail,
  BellRing, ClipboardCheck, PenLine,
  FileText, CreditCard, BookOpen,
  Star, ThumbsUp, RotateCcw,
} from 'lucide-react'

// ─────────────────────────────────────────────
// TYPES & DATA
// ─────────────────────────────────────────────

interface Node {
  id: string
  title: string
  icon: LucideIcon
  manualH: number
  autoH: number
  saving: number
  problem: string
  solution: string
  impact: string
}

interface Stage {
  id: string
  label: string
  Icon: LucideIcon
  header: string
  border: string
  text: string
  tag: string
  nodes: Node[]
}

const STAGES: Stage[] = [
  {
    id: 'captacion', label: 'Captación',
    Icon: Target,
    header: 'bg-blue-600',
    border: 'border-blue-200', text: 'text-blue-700', tag: 'bg-blue-50 text-blue-700',
    nodes: [
      {
        id: 'ads', title: 'Publicidad Digital',
        icon: Megaphone, manualH: 8, autoH: 0.5, saving: 2100,
        problem: 'Revisas métricas a diario, ajustas pujas a mano y copias los leads al CRM uno a uno. Horas que desaparecen sin mover la aguja.',
        solution: 'Las campañas se optimizan automáticamente. Cada lead entra directo al CRM con etiqueta, fuente y puntuación. Tú solo apruebas el presupuesto mensual.',
        impact: 'De 8h/mes de gestión manual a 30 min de revisión semanal.',
      },
      {
        id: 'chatbot', title: 'Chatbot Web 24/7',
        icon: MessageCircle, manualH: 5, autoH: 0, saving: 1800,
        problem: 'Pierdes leads fuera de horario. El visitante rellena el formulario y espera 24h. O llama y no contestas. Sale por donde entró.',
        solution: 'El chatbot cualifica a cada visitante en segundos: qué busca, qué presupuesto tiene, si ya es cliente. Solo te llegan los leads filtrados.',
        impact: '+30% conversión de tráfico web a lead cualificado.',
      },
      {
        id: 'whatsapp-in', title: 'WhatsApp Automático',
        icon: Smartphone, manualH: 6, autoH: 0, saving: 1500,
        problem: 'Respondes las mismas preguntas decenas de veces al mes. Cada hora que tardas, el lead se enfría y se va a la competencia.',
        solution: 'Respuesta automática en segundos con info, disponibilidad y enlace de reserva. Si necesita algo específico, el bot filtra antes de pasártelo.',
        impact: 'Tiempo de respuesta: de horas a segundos.',
      },
    ],
  },
  {
    id: 'agenda', label: 'Agenda',
    Icon: CalendarDays,
    header: 'bg-blue-700',
    border: 'border-blue-200', text: 'text-blue-800', tag: 'bg-blue-50 text-blue-800',
    nodes: [
      {
        id: 'reserva', title: 'Reserva Online 24/7',
        icon: Calendar, manualH: 12, autoH: 0, saving: 3600,
        problem: 'Recibes decenas de llamadas o WhatsApps al mes solo para reservar. Buscas hueco, confirmas, apuntas. Si cancela, deshaces todo el proceso.',
        solution: 'El cliente elige su hueco en tiempo real desde el móvil. Paga señal si quieres. Recibe confirmación automática. Sin una sola llamada.',
        impact: 'De 12h/mes en agenda a 0. Un 40% menos de cancelaciones.',
      },
      {
        id: 'confirmacion', title: 'Confirmación de Cita',
        icon: Bell, manualH: 4, autoH: 0, saving: 1200,
        problem: 'Escribes o llamas a cada cliente el día anterior para confirmar. Actualizas si cancela. Buscas quién puede ocupar ese hueco.',
        solution: 'Mensaje automático 48h antes: "¿Confirmas tu cita el jueves a las 11:30?" Si cancela, el hueco va a la lista de espera automáticamente.',
        impact: '4h/mes recuperadas. -40% cancelaciones sin aviso.',
      },
      {
        id: 'lista-espera', title: 'Lista de Espera',
        icon: ListOrdered, manualH: 3, autoH: 0, saving: 900,
        problem: 'Cuando cancela alguien, llamas uno a uno a tu lista de espera. El hueco se queda vacío mientras tanto. Ingresos perdidos.',
        solution: 'Al cancelarse un hueco, el sistema notifica automáticamente a los clientes en lista de espera por orden de prioridad. El primero que confirma, se queda el hueco.',
        impact: 'Tasa de ocupación +15%. Ingresos recuperados sin esfuerzo.',
      },
    ],
  },
  {
    id: 'onboarding', label: 'Onboarding',
    Icon: ClipboardList,
    header: 'bg-indigo-600',
    border: 'border-indigo-200', text: 'text-indigo-700', tag: 'bg-indigo-50 text-indigo-700',
    nodes: [
      {
        id: 'ficha', title: 'Ficha de Cliente',
        icon: FolderOpen, manualH: 6, autoH: 0.5, saving: 1800,
        problem: 'Papel en recepción o formulario a mano. El cliente lo rellena con prisa. Lo introduces al sistema manualmente. Lo buscas cuando lo necesitas.',
        solution: 'Antes de la primera cita, el cliente recibe un formulario por WhatsApp o email. Sus datos van directo al CRM, ya preparados para cuando llega.',
        impact: '6h/mes recuperadas. Cero papel. Historial siempre accesible.',
      },
      {
        id: 'rgpd', title: 'Documentación RGPD',
        icon: Shield, manualH: 4, autoH: 0.2, saving: 1200,
        problem: 'Imprimes, das a firmar, escaneas, archivas en carpeta. Cuando lo necesitas, buscas el papel durante 20 minutos.',
        solution: 'El cliente firma digitalmente desde su móvil antes de la primera cita. Archivado automáticamente con fecha y versión del documento.',
        impact: 'Cumplimiento legal garantizado. Sin papel. Siempre localizable.',
      },
      {
        id: 'bienvenida', title: 'Secuencia de Bienvenida',
        icon: Mail, manualH: 3, autoH: 0, saving: 900,
        problem: 'Explicas lo mismo 20 veces al mes: "cómo funciona", "qué traer", "cómo llegar", "cómo se paga". O no lo explicas y el cliente llega confundido.',
        solution: 'Al confirmar la primera cita, secuencia automática: confirmación + instrucciones + ubicación + política de cancelación. Todo en el momento justo.',
        impact: 'Clientes preparados. Menos llamadas de duda. Mejor primera impresión.',
      },
    ],
  },
  {
    id: 'operaciones', label: 'Operaciones',
    Icon: Zap,
    header: 'bg-indigo-700',
    border: 'border-indigo-200', text: 'text-indigo-800', tag: 'bg-indigo-50 text-indigo-800',
    nodes: [
      {
        id: 'recordatorio', title: 'Recordatorio Previo',
        icon: BellRing, manualH: 5, autoH: 0, saving: 1500,
        problem: 'Escribes o llamas a cada cliente antes de cada servicio. Si hay varios ese día, son varios mensajes manuales. O no lo haces y aumentan los no-shows.',
        solution: 'Secuencia automática: WhatsApp el día anterior + recordatorio 2h antes con las instrucciones específicas del servicio contratado.',
        impact: '-35% ausencias sin avisar. 5h/mes recuperadas.',
      },
      {
        id: 'checklist', title: 'Checklist de Servicio',
        icon: ClipboardCheck, manualH: 4, autoH: 0.5, saving: 1200,
        problem: 'Buscas la ficha del cliente, recuerdas qué servicio tiene, preparas el protocolo de memoria. En días con mucho trabajo, es fácil cometer errores.',
        solution: 'Cada servicio tiene su checklist automático: materiales, tiempos, notas de sesiones anteriores. Preparación en segundos.',
        impact: 'Cero errores de ejecución. Calidad constante independientemente del volumen.',
      },
      {
        id: 'registro', title: 'Registro de Trabajo',
        icon: PenLine, manualH: 6, autoH: 2, saving: 1200,
        problem: 'Escribes notas en papel, las pasas al ordenador. O las pierdes. O la próxima vez no recuerdas qué se hizo en el servicio anterior.',
        solution: 'Plantilla de registro por tipo de servicio. Historial del cliente siempre actualizado y accesible desde cualquier dispositivo.',
        impact: 'Continuidad perfecta. Historial siempre en un clic.',
      },
    ],
  },
  {
    id: 'facturacion', label: 'Facturación',
    Icon: Receipt,
    header: 'bg-blue-800',
    border: 'border-blue-200', text: 'text-blue-900', tag: 'bg-blue-50 text-blue-900',
    nodes: [
      {
        id: 'factura', title: 'Factura Automática',
        icon: FileText, manualH: 8, autoH: 0, saving: 2400,
        problem: 'Al terminar cada servicio: abres el programa, creas la factura, rellenas los datos del cliente, envías. 5 min × 40 clientes/mes = más de 3h solo en facturas.',
        solution: 'Al marcar el servicio como completado, la factura se genera y se envía automáticamente al email del cliente. Con los datos que ya están en el sistema.',
        impact: '8h/mes recuperadas. Cero errores. Cobros más rápidos.',
      },
      {
        id: 'cobro', title: 'Cobro Online',
        icon: CreditCard, manualH: 3, autoH: 0, saving: 900,
        problem: 'Cobras en efectivo que luego hay que cuadrar, esperas transferencias que a veces no llegan, o el cliente cancela sin haber pagado señal.',
        solution: 'Señal automática al reservar. Link de pago por WhatsApp al terminar. El cliente paga desde el móvil. Tú no persigues a nadie.',
        impact: '-80% impagos. Flujo de caja predecible.',
      },
      {
        id: 'contabilidad', title: 'Contabilidad Sync',
        icon: BookOpen, manualH: 4, autoH: 0, saving: 1200,
        problem: 'Exportas facturas del mes, preparas el Excel, lo mandas a la gestoría, esperas que te llamen para aclarar. Cada mes el mismo ritual.',
        solution: 'El sistema sincroniza automáticamente con tu gestoría. Los ingresos del mes siempre cuadrados. Tu gestor entra directamente al sistema.',
        impact: 'Cierre mensual en minutos en lugar de horas.',
      },
    ],
  },
  {
    id: 'retencion', label: 'Retención',
    Icon: RefreshCw,
    header: 'bg-indigo-500',
    border: 'border-indigo-200', text: 'text-indigo-700', tag: 'bg-indigo-50 text-indigo-700',
    nodes: [
      {
        id: 'nps', title: 'Encuesta Satisfacción',
        icon: Star, manualH: 2, autoH: 0, saving: 600,
        problem: 'No preguntas qué tal fue el servicio, o lo haces en persona (incómodo). Te enteras de que algo fue mal cuando ya hay una reseña negativa.',
        solution: '24h después de cada servicio: "¿Cómo fue tu experiencia? Del 1 al 10". Respuesta baja = alerta inmediata para que puedas reaccionar.',
        impact: 'Detectas problemas antes de que se conviertan en reseñas negativas.',
      },
      {
        id: 'resena', title: 'Reseñas Google Auto',
        icon: ThumbsUp, manualH: 1, autoH: 0, saving: 600,
        problem: 'Pedirle la reseña en persona es incómodo. Si el 10% lo hace, tienes suerte. El 90% de tus clientes satisfechos nunca escribe nada.',
        solution: 'Si el NPS da 8, 9 o 10 → link directo a Google Reviews por WhatsApp. Sin que nadie lo pida en persona. Sin fricción.',
        impact: '+3-5 reseñas positivas al mes de forma constante y automática.',
      },
      {
        id: 'reactivacion', title: 'Reactivación Automática',
        icon: RotateCcw, manualH: 4, autoH: 0, saving: 1500,
        problem: 'Los clientes que no vuelven desaparecen. Podrían volver con un recordatorio, pero no tienes tiempo de revisar quién lleva meses sin comprarte.',
        solution: 'Si un cliente lleva X días sin comprar o reservar, recibe un mensaje personalizado. Si lleva más tiempo, una oferta especial. Todo automático.',
        impact: '+10-15% de clientes inactivos reactivados cada mes.',
      },
    ],
  },
]

const TOTAL_SAVING = STAGES.flatMap(s => s.nodes).reduce((acc, n) => acc + n.saving, 0)
const TOTAL_HOURS  = STAGES.flatMap(s => s.nodes).reduce((acc, n) => acc + n.manualH, 0)
const TOTAL_NODES  = STAGES.flatMap(s => s.nodes).length

// ─────────────────────────────────────────────
// PRE-FILL HELPER
// ─────────────────────────────────────────────

function implementarFlujo(title: string) {
  sessionStorage.setItem('prefill_proceso', title)
  const el = document.getElementById('contacto')
  if (el) {
    window.dispatchEvent(new CustomEvent('prefill-contacto', { detail: { proceso: title } }))
    // Small delay so modal closes first
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
  } else {
    window.location.href = '/#contacto'
  }
}

// ─────────────────────────────────────────────
// NODE CARD
// ─────────────────────────────────────────────

function NodeCard({ node, stage, onClick }: { node: Node; stage: Stage; onClick: () => void }) {
  const NodeIcon = node.icon
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl border ${stage.border} bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 p-3 group cursor-pointer`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className={`w-6 h-6 rounded-lg ${stage.tag} flex items-center justify-center shrink-0`}>
          <NodeIcon size={12} />
        </div>
        <p className="text-[12px] font-semibold text-slate-800 leading-tight group-hover:text-slate-900">
          {node.title}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${stage.tag} rounded-full px-2 py-0.5 border ${stage.border}`}>
          <Clock size={8} />
          {node.manualH}h → {node.autoH}h/mes
        </span>
        <span className="text-[11px] font-bold text-blue-700">
          {node.saving.toLocaleString('es')}€/año
        </span>
      </div>
    </button>
  )
}

// ─────────────────────────────────────────────
// DETAIL MODAL
// ─────────────────────────────────────────────

function NodeModal({ node, stage, onClose }: { node: Node; stage: Stage; onClose: () => void }) {
  const NodeIcon = node.icon
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${stage.header} px-5 py-4 shrink-0`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <NodeIcon size={18} className="text-white" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium mb-0.5">{stage.label}</p>
                <h3 className="text-white font-bold text-base leading-tight">{node.title}</h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            >
              <X size={15} />
            </button>
          </div>
          {/* Stats */}
          <div className="flex gap-2 mt-4">
            <div className="bg-white/15 rounded-lg px-3 py-2 text-center flex-1">
              <p className="text-white/60 text-[10px] font-medium">Manual</p>
              <p className="text-white font-bold text-sm">{node.manualH}h/mes</p>
            </div>
            <div className="bg-white/15 rounded-lg px-3 py-2 text-center flex-1">
              <p className="text-white/60 text-[10px] font-medium">Automatizado</p>
              <p className="text-white font-bold text-sm">{node.autoH}h/mes</p>
            </div>
            <div className="bg-white/25 border border-white/20 rounded-lg px-3 py-2 text-center flex-1">
              <p className="text-white/70 text-[10px] font-medium">Ahorro/año</p>
              <p className="text-white font-bold text-sm">{node.saving.toLocaleString('es')}€</p>
            </div>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mb-1.5">Sin automatizar</p>
            <p className="text-sm text-slate-600 leading-relaxed">{node.problem}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1.5">Con Innovathera</p>
            <p className="text-sm text-slate-700 leading-relaxed">{node.solution}</p>
          </div>
          <div className={`${stage.tag} border ${stage.border} rounded-xl px-4 py-3`}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1 opacity-60">Impacto real</p>
            <p className={`text-sm font-semibold ${stage.text}`}>{node.impact}</p>
          </div>
          <button
            onClick={() => { onClose(); implementarFlujo(node.title) }}
            className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm rounded-xl py-3.5 transition-colors"
          >
            Implementar este flujo
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// STAGE COLUMN
// ─────────────────────────────────────────────

function StageColumn({ stage, onNodeClick }: { stage: Stage; onNodeClick: (n: Node, s: Stage) => void }) {
  const [open, setOpen] = useState(true)
  const StageIcon = stage.Icon

  return (
    <div className="w-full">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full ${stage.header} text-white rounded-xl px-3 py-2.5 mb-2 flex items-center justify-between gap-2 hover:opacity-90 transition-opacity`}
      >
        <span className="flex items-center gap-2 font-bold text-[12px]">
          <StageIcon size={13} />
          {stage.label}
        </span>
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-2">
          {stage.nodes.map(node => (
            <NodeCard key={node.id} node={node} stage={stage} onClick={() => onNodeClick(node, stage)} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────

export default function AutomationMap() {
  const [selected, setSelected] = useState<{ node: Node; stage: Stage } | null>(null)

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
        {[
          { value: TOTAL_NODES,    label: 'procesos automatizables' },
          { value: TOTAL_HOURS,    label: 'horas/mes recuperadas' },
          { value: Math.round(TOTAL_SAVING / 1000) + 'K€', label: 'ahorro potencial/año', raw: true },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 px-3 sm:px-4 py-4 text-center shadow-sm">
            <p className="text-xl sm:text-2xl lg:text-3xl font-black text-blue-700 tabular-nums">
              {s.raw ? s.value : (s.value as number).toLocaleString('es')}
            </p>
            <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 font-medium leading-tight">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Map grid — 1 col mobile, 2 cols sm, 3 cols lg, 6 cols xl */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {STAGES.map(stage => (
          <StageColumn
            key={stage.id}
            stage={stage}
            onNodeClick={(n, s) => setSelected({ node: n, stage: s })}
          />
        ))}
      </div>

      <p className="text-center text-[11px] text-slate-400 mt-3">
        Toca cualquier proceso para ver cómo funciona
      </p>

      {selected && (
        <NodeModal
          node={selected.node}
          stage={selected.stage}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}
